import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faFileArrowDown,
  faTrash,
  faArrowRightArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

interface ViewUploadZoneTranslations {
  remove?: string;
  mirrored?: string;
  disabled?: string;
  dragDropOrClick?: string;
  dropImageHere?: string;
  fileSizeError?: string; // Should include {size} placeholder
}

interface ViewUploadZoneProps {
  viewName: string;
  required?: boolean;
  file: File | null;
  onFileSelect: (file: File | null) => void;
  disabled?: boolean;
  mirroredPreviewUrl?: string | null;
  translations?: ViewUploadZoneTranslations;
}

const ACCEPTED_IMAGE_TYPES = "image/png, image/jpeg, image/jpg, image/webp";
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const defaultTranslations: ViewUploadZoneTranslations = {
  remove: "Remove",
  mirrored: "Mirrored",
  disabled: "Disabled",
  dragDropOrClick: "Drag & drop or click to upload",
  dropImageHere: "Drop image here",
  fileSizeError: "File size must be less than {size} MB",
};

export function ViewUploadZone({
  viewName,
  required = false,
  file,
  onFileSelect,
  disabled = false,
  mirroredPreviewUrl = null,
  translations = {},
}: ViewUploadZoneProps): React.ReactElement {
  const t = { ...defaultTranslations, ...translations };
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Recreate preview URL when component mounts with existing file
  // This handles the case where the component was unmounted and remounted
  useEffect(() => {
    if (file && !previewUrl) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
    // Cleanup when file is removed externally
    if (!file && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [file]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  function handleDrag(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || mirroredPreviewUrl) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled || mirroredPreviewUrl) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }

  function handleFile(selectedFile: File) {
    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setError(t.fileSizeError?.replace("{size}", String(MAX_FILE_SIZE_MB)) || `File size must be less than ${MAX_FILE_SIZE_MB} MB`);
      return;
    }

    setError(null);
    // Create preview URL
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    onFileSelect(selectedFile);
  }

  function handleRemove(e: React.MouseEvent) {
    e.stopPropagation();
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setError(null);
    onFileSelect(null);
    // Reset file input
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  function onUploadClick() {
    if (disabled || mirroredPreviewUrl) return;
    inputRef.current?.click();
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          {viewName}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
        {file && (
          <button
            onClick={handleRemove}
            className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faTrash} size="xs" />
            {t.remove}
          </button>
        )}
      </div>

      <div
        className={`relative border-2 border-dashed rounded-md h-32 flex items-center justify-center transition-colors ${
          disabled || mirroredPreviewUrl
            ? "cursor-not-allowed"
            : "cursor-pointer"
        } ${
          mirroredPreviewUrl
            ? "border-blue-400 bg-blue-50"
            : dragActive
              ? "border-primary bg-primary-bg"
              : error
                ? "border-red-400 bg-red-50"
                : file
                  ? "border-green-400 bg-green-50"
                  : disabled
                    ? "border-gray-200 bg-gray-100 opacity-60"
                    : "border-gray-300 hover:border-gray-400 bg-gray-50"
        }`}
        onClick={onUploadClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          className="hidden"
          type="file"
          onChange={handleChange}
          accept={ACCEPTED_IMAGE_TYPES}
          disabled={disabled || !!mirroredPreviewUrl}
        />

        {mirroredPreviewUrl ? (
          <div className="relative w-full h-full p-2">
            <img
              src={mirroredPreviewUrl}
              alt={`${viewName} mirrored preview`}
              className="w-full h-full object-contain"
              style={{ transform: "scaleX(-1)" }}
            />
            <div className="absolute top-1 right-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
              <FontAwesomeIcon icon={faArrowRightArrowLeft} size="xs" />
              <span>{t.mirrored}</span>
            </div>
          </div>
        ) : file && previewUrl ? (
          <div className="relative w-full h-full p-2">
            <img
              src={previewUrl}
              alt={`${viewName} preview`}
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <div className={`space-y-2 flex flex-col items-center ${disabled ? "text-gray-400" : "text-gray-500"}`}>
            <FontAwesomeIcon icon={faCloudArrowUp} size="lg" />
            <p className="text-xs text-center">
              {disabled ? t.disabled : t.dragDropOrClick}
            </p>
          </div>
        )}

        {dragActive && !disabled && !mirroredPreviewUrl && (
          <div className="absolute inset-0 bg-gray-200 bg-opacity-80 flex items-center justify-center rounded-lg">
            <div className="space-y-2 flex flex-col items-center">
              <FontAwesomeIcon icon={faFileArrowDown} size="lg" />
              <p className="text-xs">{t.dropImageHere}</p>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
